<template>
    <Modal>
        <div class="p-grid p-fluid p-justify-end">

            <div class="p-col-12">
                <AutoComplete v-model="selectedValue"
                              field="label"
                              class="p-mb-2"
                              :suggestions="filtered"
                              @complete="searchUsers($event)"
                              @keyup.enter="onSubmit"
                              :placeholder="this.$t('loginAs')"
                              autofocus
                              />
                <span class="text-red">{{ autocompleteStatusMessageKey ? $t(autocompleteStatusMessageKey) : '&nbsp;' }}</span>
            </div>
            <div class="p-col-6">
                <Button
                        id="continue"
                        style="margin-top: 4em;"
                        :label="this.$t('continue')"
                        @click="onSubmit()"
                        />
            </div>
        </div>
    </Modal>
</template>

<script>
    import Modal from "@/components/Shared/Modal";
    import Button from "primevue/button";
    import UserSession from "@/service/UserSession";
    import AuthenticationService from "@/service/AuthenticationService";
    import UserService from "@/service/UserService";
    import { JwtToken } from "@/utils/jwt";

    export default {
        components: {
            Modal,
            Button
        },
        data() {
            const userSession = new UserSession(localStorage);

            return {
                userSession: userSession,
                authenticationService: new AuthenticationService(userSession),
                userService: new UserService(),
                filtered: null,
                selectedValue: null,
                filterQueryTimeout: null,
                autocompleteStatusMessageKey: null,
            };
        },
        methods: {
            searchUsers: function (event) {

                if (this.filterQueryTimeout) {
                    clearTimeout(this.filterQueryTimeout);
                }

                this.filterQueryTimeout = setTimeout(() => {

                    const nameQuery = event.query.toLowerCase();
                    if (nameQuery.length) {
                        this.fetchUsers(nameQuery);
                        this.filterQueryTimeout = null;
                    }

                }, 600);
            },
            onSubmit: function () {
                const userId = this.selectedValue && this.selectedValue.id || this.userSession.userId;

                this.authenticationService.impersonate(userId, this.$route.query.continue)
                    .then(continueUrl => {
                        // localStorage now holds the impersonated user's tokens. The mnav3 cookie
                        // rewrite (OAuth2 dance to client_id=mnav3) is only needed when the user
                        // is actually about to land on a mnav3 page. If continueUrl points elsewhere
                        // -- e.g. another OAuth client's /oauth2/authorize for a downstream SSO
                        // (iNavigator), or a mnav4-internal route -- those callers handle their
                        // own auth via localStorage, so we navigate there directly.
                        const home = new URL(JwtToken.fromLocalStorage().homeUrl());
                        const isMnav3Target = !continueUrl || continueUrl.startsWith(home.origin);

                        if (!isMnav3Target) {
                            location.href = continueUrl;
                            return;
                        }

                        // OAuth2 dance to rewrite mnav3 cookies for the impersonated user.
                        // Omit `redirect_uri`: server falls back to the registered default for
                        // client_id=mnav3. `state` carries the path within mnav3 to land on.
                        const target = continueUrl
                            ? continueUrl.substring(home.origin.length)
                            : (home.pathname + home.search);
                        location.href = '/oauth2/authorize?response_type=code&client_id=mnav3&state=' + encodeURIComponent(target);
                    });
            },
            fetchUsers: function (nameContains) {
                this.autocompleteStatusMessage = null
                this.userService.getSummary(nameContains)
                    .then(response => {
                        if (!response.length) {
                            this.autocompleteStatusMessageKey = 'noUsersFound';
                        } else {
                            this.autocompleteStatusMessageKey = null;
                        }

                        this.filtered = response.map(user => ({
                            user: user,
                            label: `${user.name} [${user.username}]`,
                            id: user.id
                        }));
                    })
                    .catch(() => {
                        this.autocompleteStatusMessageKey = 'somethingWentWrong';
                        this.filtered = [];
                    });
            }
        }
    }
</script>

<style lang="scss">
    .text-red {
        color: var(--red-600);
    }
</style>

<i18n lang="yaml" locale="en">
    loginAs: "Choose user to login as"
    noUsersFound: "No users found"
</i18n>

<i18n lang="yaml" locale="dk">
    loginAs: "Vælg bruger til at logge ind som"
</i18n>
